/**
 * Liste / recherche des équipes (espace compétition)
 *
 * Recherche par nom d'équipe ou pseudo d'un membre (avec mise en évidence de
 * la portion trouvée), filtres "équipe complète" et "participe au championnat
 * en cours", tri (nom / division / effectif / ancienneté). Clic sur une
 * équipe → fiche publique existante (TeamProfileModal, via ProfileContext).
 */

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import { searchTeams, getMyTeam, type TeamSearchItem, type TeamSortOption, type MyTeam } from '../../lib/teamsApi';
import { SearchIcon } from '../../components/Icons';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

interface TeamsListPageProps {
  session: Session;
}

const SORT_OPTIONS: { value: TeamSortOption; label: string }[] = [
  { value: 'name', label: 'Nom (A → Z)' },
  { value: 'division', label: 'Division (meilleure d\'abord)' },
  { value: 'members_desc', label: 'Effectif (le plus grand d\'abord)' },
  { value: 'members_asc', label: 'Effectif (le plus petit d\'abord)' },
  { value: 'age_asc', label: 'Ancienneté (les plus anciennes d\'abord)' },
  { value: 'age_desc', label: 'Ancienneté (les plus récentes d\'abord)' },
];

// Met en évidence la portion d'un texte qui correspond à la recherche en cours.
function highlight(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="tc-search-highlight">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// Un membre "matche" la recherche si son pseudo affiché contient la requête.
function memberMatches(pseudoDisplay: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  return pseudoDisplay.toLowerCase().includes(q);
}

function ROLE_RING(role: string): string | undefined {
  if (role === 'capitaine') return '#e0a020';
  if (role === 'lieutenant') return '#8aa0b8';
  return undefined;
}

function ROLE_BADGE(role: string): string | null {
  if (role === 'capitaine') return 'C';
  if (role === 'lieutenant') return 'L';
  return null;
}

/** Ancienneté depuis la date de création : N jours | N mois | N ans (une seule unité). */
function formatTeamAge(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const created = new Date(dateStr);
  if (Number.isNaN(created.getTime())) return null;
  const now = new Date();
  if (created.getTime() > now.getTime()) return null;

  let years = now.getFullYear() - created.getFullYear();
  let months = now.getMonth() - created.getMonth();
  let days = now.getDate() - created.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (years >= 1) return years === 1 ? '1 an' : `${years} ans`;
  if (months >= 1) return months === 1 ? '1 mois' : `${months} mois`;
  const totalDays = Math.floor((now.getTime() - created.getTime()) / (24 * 3600 * 1000));
  if (totalDays <= 0) return "moins d'un jour";
  return totalDays === 1 ? '1 jour' : `${totalDays} jours`;
}

function formatClassement(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n) || n < 1) return null;
  return n === 1 ? '1er' : `${n}e`;
}

export default function TeamsListPage({ session }: TeamsListPageProps) {
  const { openTeamProfile } = useProfile();
  const [search, setSearch] = useState('');
  const [onlyComplete, setOnlyComplete] = useState(false);
  const [onlyChamp, setOnlyChamp] = useState(false);
  const [onlyPlayed, setOnlyPlayed] = useState(false);
  const [myTeam, setMyTeam] = useState<MyTeam | null>(null);
  const [myTeamLoaded, setMyTeamLoaded] = useState(false);
  const [sort, setSort] = useState<TeamSortOption>('name');
  const [teams, setTeams] = useState<TeamSearchItem[]>([]);
  const [total, setTotal] = useState(0);
  // `loading` : uniquement le chargement initial (aucune carte encore affichée).
  // `fetching` : une recherche est en cours (y compris re-recherches suite à un
  // changement de filtre/tri) — la grille de cartes reste montée pendant ce
  // temps pour que useAutoAnimate puisse animer (déplacer/ajouter/retirer) les
  // cartes existantes au lieu de tout démonter puis remonter (ce qui produisait
  // un fade-out/fade-in global au lieu d'un vrai déplacement des cartes).
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [gridRef] = useAutoAnimate<HTMLDivElement>({ duration: 250, easing: 'ease-in-out' });

  const runSearch = useCallback((
    s: string,
    complete: boolean,
    champ: boolean,
    played: boolean,
    sortOpt: TeamSortOption,
    token?: string,
  ) => {
    setFetching(true);
    setError(null);
    // Le nombre total d'équipes reste de l'ordre de quelques centaines : on les
    // récupère toutes en une seule page plutôt que d'ajouter une pagination.
    searchTeams({
      search: s || undefined,
      complete,
      champ,
      played,
      sort: sortOpt,
      limit: 500,
      token: played ? token : undefined,
    })
      .then((data) => {
        setTeams(data.teams);
        setTotal(data.total);
      })
      .catch(() => setError('Impossible de charger les équipes'))
      .finally(() => { setFetching(false); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!session.token) {
      setMyTeam(null);
      setMyTeamLoaded(true);
      return;
    }
    getMyTeam(session.token)
      .then((d) => setMyTeam(d.team))
      .catch(() => setMyTeam(null))
      .finally(() => setMyTeamLoaded(true));
  }, [session.token]);

  useEffect(() => {
    if (!myTeamLoaded) return;
    if (onlyPlayed && !myTeam) setOnlyPlayed(false);
  }, [myTeamLoaded, myTeam, onlyPlayed]);

  useEffect(() => {
    if (!myTeamLoaded) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(search, onlyComplete, onlyChamp, onlyPlayed, sort, session.token);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, onlyComplete, onlyChamp, onlyPlayed, sort, myTeamLoaded, runSearch, session.token]);

  return (
    <div className="compet-page">
      <div className="compet-page-header">
        <div className="live-ranking-search" style={{ display: 'flex', alignItems: 'center', gap: 6, width: 240 }}>
          <SearchIcon size={16} />
          <input
            type="text"
            placeholder="Nom d'équipe ou de joueur…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: 'none', background: 'none', outline: 'none', width: '100%', color: 'inherit', font: 'inherit' }}
          />
        </div>
        <label className="cia-memo-filter--check" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={onlyComplete} onChange={(e) => setOnlyComplete(e.target.checked)} />
          Équipe complète
        </label>
        <label className="cia-memo-filter--check" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={onlyChamp} onChange={(e) => setOnlyChamp(e.target.checked)} />
          Championnat en cours
        </label>
        {myTeam && (
          <label
            className="cia-memo-filter--check"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title={`Défis déjà joués contre d'autres équipes (${myTeam.nom})`}
          >
            <input type="checkbox" checked={onlyPlayed} onChange={(e) => setOnlyPlayed(e.target.checked)} />
            Déjà affrontées
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Trier par
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as TeamSortOption)}
            style={{ background: 'var(--bg-secondary)', color: 'inherit', border: '1px solid var(--border-color)', borderRadius: 6, padding: '4px 6px' }}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <span className="live-ranking-count">
          {total} équipe{total > 1 ? 's' : ''}
          {fetching && !loading && <span className="tc-refresh-spinner" aria-hidden="true" />}
        </span>
      </div>
      <div className="compet-content">
        {loading && <div className="compet-loading">Chargement…</div>}
        {!loading && error && teams.length === 0 && <div className="compet-error">{error}</div>}
        {!loading && !fetching && !error && teams.length === 0 && (
          <div className="compet-empty">
            {onlyPlayed
              ? 'Aucune équipe affrontée ne correspond à ces critères'
              : 'Aucune équipe ne correspond à ces critères'}
          </div>
        )}
        {/* La grille reste montée pendant les re-recherches (fetching) pour que
            useAutoAnimate anime le déplacement/ajout/retrait des cartes au lieu
            d'un fade-out/fade-in complet dû au démontage du conteneur. */}
        {!loading && teams.length > 0 && (
          <div className="tc-teams-grid" ref={gridRef}>
            {teams.map((team) => {
              const members = team.membersDetailed?.length
                ? team.membersDetailed
                : team.members.map((pseudo) => ({ pseudo, pseudo_display: pseudo, role: '' }));
              const showAge = sort === 'age_asc' || sort === 'age_desc';
              const ageLabel = showAge ? formatTeamAge(team.date) : null;
              const rankLabel = sort === 'division' ? formatClassement(team.division?.classement) : null;
              const divisionLabel = team.division
                ? team.division.nom
                : (sort === 'division' ? 'Hors championnat' : null);
              const subtitleParts = [divisionLabel, rankLabel, ageLabel].filter(Boolean);
              const subtitleTitle = team.division
                ? `${team.division.nom} au championnat par équipes`
                : (sort === 'division' ? 'Non inscrite au championnat par équipes en cours' : undefined);
              return (
                <button
                  key={team.id}
                  className="competition-card"
                  style={{ width: '100%', textAlign: 'left', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}
                  onClick={() => openTeamProfile(team.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
                    <PlayerAvatar
                      pseudo={team.nom}
                      size={44}
                      photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(team.nom)}?size=88`}
                    />
                    <div className="competition-card-text" style={{ flex: 1, minWidth: 0 }}>
                      <span className="competition-card-title" title={team.nom}>{highlight(team.nom, search)}</span>
                      {subtitleParts.length > 0 && (
                        <span className="competition-card-subtitle" title={subtitleTitle}>
                          {subtitleParts.join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    {members.map((m) => {
                      const ring = ROLE_RING(m.role || '');
                      const badge = ROLE_BADGE(m.role || '');
                      const matched = memberMatches(m.pseudo_display || m.pseudo, search);
                      return (
                        <div
                          key={m.pseudo}
                          style={{ position: 'relative', lineHeight: 0 }}
                          title={`${m.pseudo_display || m.pseudo}${m.role ? ` — ${m.role}` : ''}`}
                        >
                          <div
                            style={{
                              borderRadius: '50%',
                              padding: ring || matched ? 2 : 0,
                              border: ring ? `2px solid ${ring}` : matched ? '2px solid var(--accent, #4a90d9)' : 'none',
                              boxSizing: 'content-box',
                            }}
                          >
                            <PlayerAvatar pseudo={m.pseudo} size={28} />
                          </div>
                          {badge && (
                            <span
                              style={{
                                position: 'absolute', bottom: -2, right: -2, fontSize: 9, fontWeight: 700,
                                background: ring, color: '#1a1a1a', borderRadius: '50%', width: 14, height: 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                                border: '1px solid var(--bg-secondary)',
                              }}
                            >
                              {badge}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <span className="competition-card-subtitle" style={{ fontSize: '0.78rem' }}>
                    {members.map((m, i) => {
                      const roleColor = ROLE_RING(m.role || '');
                      return (
                        <Fragment key={m.pseudo}>
                          {i > 0 && ', '}
                          <span
                            style={roleColor ? { color: roleColor, fontWeight: 600 } : undefined}
                            title={m.role === 'capitaine' || m.role === 'lieutenant' ? m.role : undefined}
                          >
                            {highlight(m.pseudo_display || m.pseudo, search)}
                          </span>
                        </Fragment>
                      );
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
